import { RxCollection } from 'rxdb';
import { defer } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import {
	CombinedFeedbackData,
	DriverCarDetailsDto,
	OptionalFeedbackForBookingDto,
} from '@mopla/data-models';

import {
	EFeedbacksActionTypes,
	fetchMoplaLegFeedbackResult,
	TFetchMoplaLegFeedbackAction,
} from '../actions/feedbacksActions';
import { Effect } from '../business-logic';
import { ofType } from '../operators/ofType';

export const fetchMoplaLegFeedbackEffect: Effect<
	TFetchMoplaLegFeedbackAction
> = (actions$, deps) =>
	actions$.pipe(
		ofType(EFeedbacksActionTypes.FetchMoplaLegFeedback),
		concatMap(({ payload }) =>
			defer(async () => {
				const feedbacksCol = deps.db[
					'feedbacks'
				] as RxCollection<CombinedFeedbackData>;
				const { moplaLegId } = payload;

				try {
					const [driverCarDetails, feedback] = await Promise.all([
						deps.api.get<DriverCarDetailsDto>(
							`/api/feedback/driver-car-details/${moplaLegId}`
						),
						deps.api.get<OptionalFeedbackForBookingDto>(
							`/api/feedback/by-booking-leg/${moplaLegId}`
						),
					]);

					await feedbacksCol.upsert({
						legId: moplaLegId,
						driverCarDetails,
						feedback: feedback,
					});

					return fetchMoplaLegFeedbackResult();
				} catch (error: any) {
					deps.Sentry.captureException(error);
					return fetchMoplaLegFeedbackResult({ error });
				}
			})
		)
	);

export const wipeMoplaFeedbacksEffect: Effect = (actions$, deps) =>
	actions$.pipe(
		ofType(EFeedbacksActionTypes.WipeMoplaFeedbacksData),
		concatMap(() =>
			defer(async () => {
				const feedbacksCol = deps.db.collections[
					'feedbacks'
				] as RxCollection<CombinedFeedbackData>;

				const docsCount = await feedbacksCol.find().exec();

				/** The idea is to run a wipe procedure once an app is started and cleanup feedbacks storage if there's too much data */
				if (docsCount.length > 50) {
					await feedbacksCol.bulkRemove(docsCount.map((d) => d.legId));
				}
			})
		)
	);
